import type { Partner } from "@expert/shared-types";
import type { WritableDraft } from "immer";

import { create } from "zustand";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import type { ExpertAssistMessage } from "../shared-types";

import { fallbackPartner } from "../utils/fallbackValues";
import { fallbackDefaultMessages } from "./fallbackDefaultMessages";

interface Expert {
    expertName: string;
    expertId: string;
}

interface TimelineMetaProps {
    partner: Partner;
    callSid?: string;
    chatId?: string;
    lob?: string;
}

interface TimelineState {
    partner: Partner;
    sessionId: string;
    callSid?: string;
    chatId?: string;
    lob?: string;

    messages: ExpertAssistMessage[];

    autoCompleteSuggestions: string[];
    showAutocompleteSuggestions: boolean;
}
export interface TimelineStore {
    // Applies to all timelines, not just the current one
    expertId: string;
    expertName: string;
    setExpert: ({ expertId, expertName }: Expert) => void;
    defaultMessages: ExpertAssistMessage[];
    setDefaultMessages: (defaultMessages: ExpertAssistMessage[]) => void;
    shouldAutoOpenToolbox: boolean;
    setShouldAutoOpenToolbox: (val: boolean) => void;

    currentSessionId: string;
    setCurrentSessionId: (sessionId: string) => void;
    useDefaultTimelineKey: boolean;
    setUseDefaultTimelineKey: (useDefaultTimelineKey: boolean) => void;
    timelines: Record<string, TimelineState>;

    // Methods for managing multiple timeline instances
    setTimelineProperties: (sessionId: string, { partner, callSid, chatId, lob }: TimelineMetaProps) => void;

    addMessage: (sessionId: string, message: ExpertAssistMessage) => void;
    addMessages: (sessionId: string, messages: ExpertAssistMessage[]) => void;
    removeMessages: (sessionId: string, messageIds: string[]) => void;
    resetMessages: (sessionId: string, messages: ExpertAssistMessage[]) => void;
    updateMessage: (sessionId: string, id: string, message: Partial<ExpertAssistMessage>) => void;
    setIsMessageUnread: (sessionId: string, messageId: string, isUnread: boolean) => void;
    markAllMessagesAsRead: (sessionId: string) => void;

    setNewSuggestions: (sessionId: string, suggestions: string[]) => void;
    setShowAutocompleteSuggestions: (sessionId: string, val: boolean) => void;

    getTimelineKey: (sessionId: string, useDefaultTimelineKey?: boolean) => string;
    getTimelineState: (sessionId: string) => TimelineState;
    clearTimelineState: (sessionId: string) => void;
}

const defaultTimelineKey = "default";

export const useTimelineStore = create<TimelineStore>()(
    subscribeWithSelector(
        immer(
            devtools(
                persist(
                    (set, get) => ({
                        // Initial state for the timeline store
                        expertId: "",
                        expertName: "",
                        currentSessionId: "",
                        defaultMessages: fallbackDefaultMessages,
                        useDefaultTimelineKey: true,
                        shouldAutoOpenToolbox: true,
                        timelines: {},

                        // Methods for managing the timeline store
                        setCurrentSessionId: (sessionId: string) =>
                            set({ currentSessionId: sessionId }, false, "setCurrentSessionId"),
                        setUseDefaultTimelineKey: (useDefaultTimelineKey: boolean) =>
                            set({ useDefaultTimelineKey }, false, "setUseDefaultTimelineKey"),
                        getTimelineKey: (
                            sessionId: string,
                            useDefaultTimelineKey: boolean = get().useDefaultTimelineKey,
                        ): string => (useDefaultTimelineKey ? defaultTimelineKey : sessionId),
                        setExpert: ({ expertId, expertName }: Expert) =>
                            set({ expertId, expertName }, false, "setExpert"),
                        setDefaultMessages: (defaultMessages: ExpertAssistMessage[]) => {
                            set(
                                (state) => {
                                    state.defaultMessages = defaultMessages;
                                },
                                false,
                                "setDefaultMessages",
                            );
                        },

                        // Methods for managing individual timeline instances
                        getTimelineState: (sessionId: string) => {
                            const timelineKey = get().getTimelineKey(sessionId);
                            return (
                                get().timelines[timelineKey] ?? {
                                    partner: fallbackPartner,
                                    sessionId: "",
                                    callSid: undefined,
                                    chatId: undefined,
                                    lob: undefined,
                                    messages: [],
                                    autoCompleteSuggestions: [],
                                    showAutocompleteSuggestions: false,
                                }
                            );
                        },
                        setTimelineProperties: (
                            sessionId: string,
                            { partner, callSid, chatId, lob }: TimelineMetaProps,
                        ) =>
                            set(
                                (state) =>
                                    setTimelineStateForSession(state, sessionId, {
                                        partner,
                                        sessionId,
                                        callSid,
                                        chatId,
                                        lob,
                                    }),
                                false,
                                "setTimelineProperties",
                            ),

                        addMessage: (sessionId: string, message: ExpertAssistMessage) => {
                            set(
                                (state) => {
                                    const timeline = state.getTimelineState(sessionId);
                                    setTimelineStateForSession(
                                        state,
                                        sessionId,
                                        {
                                            messages: [...timeline.messages, message],
                                        },
                                        timeline,
                                    );
                                },
                                false,
                                "addMessage",
                            );
                        },
                        addMessages: (sessionId: string, messages: ExpertAssistMessage[]) => {
                            set(
                                (state) => {
                                    const timeline = state.getTimelineState(sessionId);
                                    setTimelineStateForSession(
                                        state,
                                        sessionId,
                                        {
                                            messages: [...timeline.messages, ...messages],
                                        },
                                        timeline,
                                    );
                                },
                                false,
                                "addMessages",
                            );
                        },
                        removeMessages: (sessionId: string, messageIds: string[]) => {
                            set(
                                (state) => {
                                    const timeline = state.getTimelineState(sessionId);
                                    const idSet = new Set(messageIds);
                                    setTimelineStateForSession(
                                        state,
                                        sessionId,
                                        {
                                            messages: timeline.messages.filter((msg) => !idSet.has(msg.id)),
                                        },
                                        timeline,
                                    );
                                },
                                false,
                                "removeMessages",
                            );
                        },
                        resetMessages: (sessionId: string, messages: ExpertAssistMessage[]) => {
                            set(
                                (state) =>
                                    setTimelineStateForSession(state, sessionId, {
                                        messages: [...messages],
                                    }),
                                false,
                                "resetMessages",
                            );
                        },
                        updateMessage: (
                            sessionId: string,
                            messageId: string,
                            messageUpdates: Partial<ExpertAssistMessage>,
                        ) => {
                            set(
                                (state) => {
                                    const timeline = state.getTimelineState(sessionId);
                                    setTimelineStateForSession(
                                        state,
                                        sessionId,
                                        {
                                            messages: timeline.messages.map((message) =>
                                                message.id === messageId ? { ...message, ...messageUpdates } : message,
                                            ),
                                        },
                                        timeline,
                                    );
                                },
                                false,
                                "updateMessage",
                            );
                        },
                        setIsMessageUnread: (sessionId: string, messageId: string, isUnread: boolean) => {
                            set(
                                (state) => {
                                    const timeline = state.getTimelineState(sessionId);
                                    setTimelineStateForSession(
                                        state,
                                        sessionId,
                                        {
                                            messages: timeline.messages.map((message) =>
                                                message.id === messageId ? { ...message, isUnread } : message,
                                            ),
                                        },
                                        timeline,
                                    );
                                },
                                false,
                                "setIsMessageUnread",
                            );
                        },
                        markAllMessagesAsRead: (sessionId: string) => {
                            set(
                                (state) => {
                                    const timeline = state.getTimelineState(sessionId);
                                    setTimelineStateForSession(
                                        state,
                                        sessionId,
                                        {
                                            messages: timeline.messages.map((message) =>
                                                message.isUnread === true ? { ...message, isUnread: false } : message,
                                            ),
                                        },
                                        timeline,
                                    );
                                },
                                false,
                                "markAllMessagesAsRead",
                            );
                        },
                        setShouldAutoOpenToolbox: (val: boolean) => {
                            set({ shouldAutoOpenToolbox: val }, false, "setShouldAutoOpenToolbox");
                        },
                        setNewSuggestions: (sessionId: string, values: string[]) => {
                            set(
                                (state) =>
                                    setTimelineStateForSession(state, sessionId, {
                                        autoCompleteSuggestions: [...values],
                                    }),
                                false,
                                "setNewSuggestions",
                            );
                        },
                        setShowAutocompleteSuggestions: (sessionId: string, val: boolean) => {
                            set(
                                (state) =>
                                    setTimelineStateForSession(state, sessionId, {
                                        showAutocompleteSuggestions: val,
                                    }),
                                false,
                                "setShowAutocompleteSuggestions",
                            );
                        },
                        clearTimelineState: (sessionId: string) => {
                            set(
                                (state) => {
                                    const timelineKey = state.getTimelineKey(sessionId);
                                    if (timelineKey === defaultTimelineKey) {
                                        // If clearing the default timeline, reset to initial state rather than deleting it
                                        state.timelines[timelineKey] = {
                                            partner: fallbackPartner,
                                            sessionId: "",
                                            callSid: undefined,
                                            chatId: undefined,
                                            lob: undefined,
                                            messages: [],
                                            autoCompleteSuggestions: [],
                                            showAutocompleteSuggestions: false,
                                        };
                                    } else {
                                        delete state.timelines[timelineKey];
                                    }
                                },
                                false,
                                "clearTimelineState",
                            );
                        },
                    }),
                    {
                        name: "timelineStore",
                        partialize: (state: TimelineStore) => ({
                            // Only persist the shouldAutoOpenToolbox property
                            shouldAutoOpenToolbox: state.shouldAutoOpenToolbox,
                        }),
                    },
                ),
                { enabled: import.meta.env.VITE_ENV_TYPE !== "production", store: "expert-assist", name: "timeline" },
            ),
        ),
    ),
);

function setTimelineStateForSession(
    state: WritableDraft<TimelineStore>,
    sessionId: string,
    data: Partial<TimelineState>,
    currentTimelineState?: TimelineState,
) {
    const timelineKey = state.getTimelineKey(sessionId);
    // Don't get the timeline state again if it's already passed in
    currentTimelineState ??= state.getTimelineState(sessionId);
    state.timelines[timelineKey] = {
        ...currentTimelineState,
        ...data,
    };
}
